import * as Sentry from '@sentry/react';

import { error as errorLog, log } from '@/lib/log';
import { getAddressCompletionUrl, getBundleRef } from '@/lib/mapping';
import { HTTP_METHODS } from '@/lib/http.d';
import { sortObject } from '@/lib/util';
import Auth from '@/lib/authentication';
import type { Address } from '@/components/Store.d';
import type { GetAccountSubscriptionsResponse } from '@/lib/util.d';

/**
 *
 * @param {String} method
 * @param {String} url
 * @param {RequestInit} fetchOptions
 * @return {*}
 */
async function _fetch(method: string, url: string, fetchOptions?: RequestInit) {
  Sentry.addBreadcrumb({
    category: 'request',
    message: `URL: ${url}, method: ${method}`,
    level: 'info'
  });

  method = method.toUpperCase();

  const options = {
    ...fetchOptions,
    method: method
  };

  let response;

  try {
    response = await fetch(url, options);

    Sentry.addBreadcrumb({
      category: 'response',
      message: `Status-Code: ${response.status}, Status-Message: ${response.statusText || JSON.stringify(response)}`,
      level: !response.ok ? 'error' : 'info'
    });
  } catch (err) {
    const isError = err instanceof Error;

    Sentry.addBreadcrumb({ category: 'fetchError', message: isError ? err.message : JSON.stringify(err), level: 'error' });

    return {
      ok: false,
      json: function () {
        return { message: isError ? err.message : JSON.stringify(err) };
      }
    };
  }

  return response;
}

/**
 *
 * @param {String} url
 * @param {Object} fetchOptions
 * @return {*}
 */
async function get(url: string, fetchOptions?: RequestInit) {
  return await _fetch(HTTP_METHODS.GET, url, fetchOptions);
}

/**
 *
 * @param {String} url
 * @param {Object} fetchOptions
 * @return {*}
 */
async function post(url: string, fetchOptions?: RequestInit) {
  return await _fetch(HTTP_METHODS.POST, url, fetchOptions);
}

/**
 *
 * @param {string} oauthTokenID
 */
async function authenticate(oauthTokenID: string) {
  const url = import.meta.env.VITE_APP_AUTHENTICATION_URL;

  const response = await post(url, {
    mode: 'cors',
    headers: {
      Authorization: oauthTokenID,
      'Content-Type': 'application/json'
    }
  });

  const json = await response.json();

  return response.ok ? json : json?.message ? Promise.reject(new Error(json.message)) : Promise.reject(new Error('No JSON result'));
}

/**
 *
 * @param {Record<string, unknown>} body
 */
async function registerWithToken(body: Record<string, unknown>) {
  const authUrl = import.meta.env.VITE_APP_REGISTER_USER_URL;

  const response = await post(`${authUrl}`, {
    body: new URLSearchParams(body as Record<string, string>).toString(),
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
      // "X-Requested-With": "XMLHttpRequest"
    }
  });

  log('registerWithToken', response);

  // Workaround to retrieve ErrorMessages response alongside a status code 400
  const json = await response.json();

  return response.ok ? json : json?.message ? Promise.reject(new Error(json.message)) : Promise.reject(new Error('No JSON result'));
}

/**
 * Purchase a bundle
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function completePurchase(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/completepurchase`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Approves a bundle purchase with PayPal
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function completeAsyncPurchase(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/completeasyncpurchase`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();

  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Creates a cart for supporter purchases
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function createCart(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/createcart`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Performs a single payment
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function completeSinglePurchase(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/supportersinglepayment`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Prepares payment with Klarna (sofort)
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function prepareSofort(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/preparesofort`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Initiates payment with Klarna (sofort)
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function initiateSofort(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/initiatesofort`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Completes payment with Klarna (sofort)
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function completeSofort(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/completesofort`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * Saves address data
 * @param {Record<string, unknown>} data
 * @param {string} oauthTokenID
 */
async function setAddress(data: Record<string, unknown>, oauthTokenID: string) {
  const url = `${import.meta.env.VITE_APP_DST_API_URL}/address`;
  const response = await post(`${url}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: oauthTokenID
    },
    body: data ? JSON.stringify(data) : null
  });
  const json = await response.json();
  return response.ok ? json : Promise.reject(new Error(json.message));
}

/**
 * @param {string} bundleRef
 */
async function getBundle(bundleRef = '') {
  // const navigate = useNavigate();

  if (!bundleRef) {
    bundleRef = parseBundleReference();
  }

  const url = import.meta.env.VITE_APP_BACKEND_API_URL;

  const response = await get(`${url}?bundleref=${bundleRef}`, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  const json = await response.json();

  return response.ok ? json : json?.message ? Promise.reject(new Error(json.message)) : Promise.reject(new Error('No JSON result'));
}

/**
 *
 * @param {string} zipCode
 */
async function getZipCode(zipCode: string) {
  const apiUrl = getAddressCompletionUrl('ZipCode', { zipCodePart: zipCode });

  const response = await get(`${apiUrl}`, {
    headers: {
      'Content-Type': 'application/json',
      'Keep-Alive': 'timeout=5, max=1000'
    }
  });

  if (response.ok) {
    const { results } = await response.json();
    const data = sortObject(results, 'zipCode');

    log('Zip Codes', data);

    return data;
  }

  /* return Promise.reject(new Error("No JSON result")); */
  errorLog('No results');
  return [];
}

/**
 *
 * @param {string} city
 */
async function getCity(city: string) {
  const apiUrl = getAddressCompletionUrl('City', {
    cityDistrictPart: city
  });

  const response = await get(`${apiUrl}`, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (response.ok) {
    const { results } = await response.json();
    const data = sortObject(results, 'name');

    log('Cities', data);

    return data;
  }

  /* return Promise.reject(new Error("No JSON result")); */
  errorLog('No results');
  return [];
}

/**
 *
 * @param {Object} addressObject
 */
async function getStreet(addressObject: Address) {
  const apiUrl = getAddressCompletionUrl('Street', addressObject);

  const response = await get(`${apiUrl}`, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (response.ok) {
    const { results } = await response.json();
    const data = sortObject(results, 'street');

    log('Streets', data);

    return data;
  }

  /* return Promise.reject(new Error("No JSON result")); */
  errorLog('No results');
  return [];
}

/**
 * @returns {string}
 */
function parseBundleReference(): string {
  const pathName =
    window.location.pathname
      .split('/')
      .filter((fragment) => fragment)
      .pop() || '';

  return getBundleRef(pathName);
}

/**
 * Validates voucher code
 * @param {String} voucherCode
 */
async function validateVoucher(voucherCode: string) {
  const url = import.meta.env.VITE_APP_VOUCHER_VALIDATION_URL;

  const response = await get(`${url}?voucherCode=${voucherCode}`, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return response.ok ? await response.json() : { isValid: false };
}

/**
 * Checks whether contract reference can be moved
 * @param {String} contractRef
 * @param {String} idToken
 */
async function checkMoveSubscription(contractRef: string, idToken = '') {
  const url = import.meta.env.VITE_APP_CHECK_MOVE_SUBSCRIPTION_URL;

  if (!idToken) {
    idToken = await Auth.getOauthToken();
  }

  const response = await get(`${url}?contractref=${contractRef}`, {
    headers: {
      Authorization: idToken,
      'Content-Type': 'application/json'
    }
  });

  return await response.json();
}

/**
 * Checks if entitlements have already been acquired from a different source
 * @param {String | String[]} source
 * @param {String} idToken
 */
async function checkAlreadyAcquiredEntitlements(source: string | string[], idToken = '') {
  const sources = Array.isArray(source) ? source : [ source ];
  const url = new URL(import.meta.env.VITE_APP_CHECK_ALREADY_ACQUIRED_URL);

  for (const src of sources) {
    url.searchParams.append('source', src);
  }

  if (!idToken) {
    idToken = await Auth.getOauthToken();
  }

  const response = await get(String(url), {
    headers: {
      Authorization: idToken,
      'Content-Type': 'application/json'
    }
  });

  return await response.json();
}

/**
 * Retrieves account subscription for a UGUID
 * @param {boolean} filter - only returns active subscriptions when true
 * @returns {Promise<object>}
 */
async function getAccountSubscriptions(filter = true): Promise<GetAccountSubscriptionsResponse> {
  const url = `${String(import.meta.env.VITE_APP_ACCOUNT_SUBSCRIPTION_URL)}?filter=${filter}`;
  const oauthToken = await Auth.getOauthToken();

  const response = await get(url, {
    headers: {
      Authorization: oauthToken,
      'Content-Type': 'application/json'
    }
  });

  const json: GetAccountSubscriptionsResponse = await response.json();

  return response.ok ? json || [] : json?.message ? Promise.reject(new Error(json.message)) : Promise.reject(new Error('No JSON result'));
}

/**
 * Retrieves the order history of UGUID
 *
 * @returns {Promise<Record<string, unknown>>} orders/subscriptions of user
 */
async function getOrderHistory() {
  const searchParams = new URLSearchParams({
    currentPage: '1',
    rowsPerPage: '30',
    isAscending: 'false'
  });

  const url = `${String(import.meta.env.VITE_APP_ORDER_HISTORY_URL)}?${searchParams.toString()}`;
  const oauthToken = await Auth.getOauthToken();

  const response = await get(url, {
    headers: {
      Authorization: oauthToken,
      'Content-Type': 'application/json'
    }
  });

  const json = await response.json();

  return response.ok ? json?.orders || [] : json?.message ? Promise.reject(new Error(json.message)) : Promise.reject(new Error('No JSON result'));
}

export {
  authenticate,
  checkAlreadyAcquiredEntitlements,
  checkMoveSubscription,
  completeAsyncPurchase,
  completePurchase,
  completeSinglePurchase,
  completeSofort,
  createCart,
  get,
  getAccountSubscriptions,
  getBundle,
  getCity,
  getOrderHistory,
  getStreet,
  getZipCode,
  initiateSofort,
  post,
  prepareSofort,
  registerWithToken,
  setAddress,
  validateVoucher
};
